import { useCallback, useEffect, useState } from "react"
import { Box, Dialog, HStack, Input, Portal, Spinner, Stack, Textarea, VStack } from "@chakra-ui/react"
import { TiWarningOutline } from "react-icons/ti"
import { Button, Select, Text } from "@/components"
import { Deal } from "@/types"
import { formatDollar } from "@/utils"
import {
  DmAutoSubmitResult,
  DmDailyTotal,
  DmDealAmounts,
  DmPaymentAccount,
  DmPaymentType,
  dmSubmitPayment,
  getDmPaymentAccounts,
  getDmPaymentContext,
  PaymentsApiError,
} from "@/api/payments"

// Deal-manager-only panel rendered in the view-as-client Payment Center.
// Mirrors the OG "Set Pending Liquidity" admin flow: stage a pending amount
// against the deal (pending + sent must stay within the OG-set total) and run
// the same auto-submit chain (active-bank gate, in-flight guard, payments-server
// per-payment cap + daily wire headroom).
//
// The DM picks the account (Bill, 2026-10-15), as at the OG's Send Check Request: the
// payments server lists what this payment may go to. The exercise defaults to the deal's
// company account when a DM added one (sent on the client's behalf, never staged); the
// liquidity goes to one of the client's own accounts, never a company's unless Bill
// excepted the deal.

// The OG's description reads "<Company> <Exercise|Liquidity> <shares> Shs".
const typeFromDescription = (d: string | null | undefined): DmPaymentType | null => {
  const m = /\b(Exercise|Liquidity) [\d,]+ Shs$/.exec(String(d ?? "").trim())
  return m ? (m[1].toLowerCase() as DmPaymentType) : null
}

const accountLabel = (a: DmPaymentAccount) => {
  const who = a.kind === "company" ? `${a.account_holder_name ?? "Company"} @ ` : ""
  const kind = a.kind === "company" ? "company account" : a.kind === "dm_added" ? "added by a DM" : "client's connected account"
  return `${who}${a.bank_name ?? "Bank"}${a.last4 ? ` ****${a.last4}` : ""} (${kind})`
}

interface Props {
  currentDeal: Deal
  hasConnectedBank: boolean
  hasPendingPayment: boolean
  onSubmitted: () => void
}

export const DmSubmitPanel = ({ currentDeal, hasConnectedBank, hasPendingPayment, onSubmitted }: Props) => {
  const [contextLoading, setContextLoading] = useState(false)
  const [dealAmounts, setDealAmounts] = useState<DmDealAmounts | null>(null)
  const [dailyTotal, setDailyTotal] = useState<DmDailyTotal | null>(null)
  const [contextError, setContextError] = useState<string | null>(null)

  const [amount, setAmount] = useState("")
  const [description, setDescription] = useState("")
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<DmAutoSubmitResult | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [paymentType, setPaymentType] = useState<DmPaymentType>("liquidity")
  const [accounts, setAccounts] = useState<DmPaymentAccount[]>([])
  const [accountId, setAccountId] = useState("")
  const [accountsError, setAccountsError] = useState<string | null>(null)

  const esoId = currentDeal?.esoId

  const loadContext = useCallback(async () => {
    if (!esoId) return
    setContextLoading(true)
    setContextError(null)
    try {
      const ctx = await getDmPaymentContext(esoId)
      setDealAmounts(ctx.deal)
      setDailyTotal(ctx.dailyTotal)
      const staged = ctx.deal.pendingAmount ?? 0
      if (staged > 0) {
        setAmount(String(staged))
        setDescription(ctx.deal.pendingDescription ?? "")
        const t = typeFromDescription(ctx.deal.pendingDescription)
        if (t) setPaymentType(t)
      }
    } catch (e: any) {
      setContextError(e instanceof PaymentsApiError ? e.message : (e?.message ?? "Failed to load payment context"))
    } finally {
      setContextLoading(false)
    }
  }, [esoId])

  // The accounts this payment may go to, with the payments server's default picked.
  const loadAccounts = useCallback(async () => {
    if (!esoId) return
    setAccountsError(null)
    try {
      const list = await getDmPaymentAccounts(esoId, paymentType)
      setAccounts(list.accounts)
      setAccountId(list.defaultAccountId ?? list.accounts[0]?.id ?? "")
    } catch (e: any) {
      setAccounts([])
      setAccountId("")
      setAccountsError(e?.response?.data?.error ?? e?.message ?? "Could not load the client's accounts")
    }
  }, [esoId, paymentType])

  useEffect(() => {
    setDealAmounts(null)
    setDailyTotal(null)
    setResult(null)
    setSubmitError(null)
    setAmount("")
    setDescription("")
    setPaymentType("liquidity")
    loadContext()
  }, [loadContext])

  useEffect(() => { loadAccounts() }, [loadAccounts])

  if (!esoId) return null

  const chosen = accounts.find((a) => a.id === accountId) ?? null
  const toCompany = chosen?.kind === "company"

  const staged = dealAmounts?.pendingAmount ?? currentDeal.pendingAmount ?? 0
  const total = dealAmounts?.totalAmount ?? 0
  const sent = dealAmounts?.sentAmount ?? 0
  const parsedAmount = Number(amount)
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0
  // Same invariant setPendingAmount enforces server-side: pending + sent <= total,
  // with the same half-cent tolerance for float residue (62910.54 + 63000 !== 125910.54).
  const exceedsTotal = amountValid && parsedAmount + sent > total + 0.005
  const remaining = dailyTotal?.available_remaining
  const exceedsDaily = amountValid && remaining != null && parsedAmount > remaining

  // A hold refuses every submission server-side (409); say so up front instead of
  // letting the DM find out from the error after clicking Submit (Cato 36854).
  const onHold = !!dealAmounts?.paymentHoldAt

  const blockers: string[] = []
  if (onHold) blockers.push(`Payments are ON HOLD for this deal: ${dealAmounts?.paymentHoldReason || "no reason recorded"}. Nothing can be submitted until the hold is lifted (ask Bill).`)
  if (hasPendingPayment) blockers.push("A payment is already in flight for this client — resolve it before submitting another.")
  // Only the client's connected account waits for a connect; a company account or one a DM
  // added is paid straight away.
  if (!hasConnectedBank && (!chosen || chosen.kind === "connected")) blockers.push("Client has no connected bank — the staged amount auto-submits when they connect.")
  if (accountsError) blockers.push(`Could not load the client's accounts: ${accountsError}`)
  if (exceedsTotal) blockers.push(`Amount + already-sent (${formatDollar(sent)}) exceeds the deal total (${formatDollar(total)}). Size the total in the OG first (SetTotalPayments).`)
  if (exceedsDaily) blockers.push(`Amount exceeds today's remaining wire headroom (${formatDollar(remaining!)}). It will be rejected — submit a smaller tranche or wait for the next banking day.`)

  const canSubmit = amountValid && !onHold && !exceedsTotal && !exceedsDaily && !hasPendingPayment && !submitting && !accountsError

  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitError(null)
    setResult(null)
    try {
      const autoSubmit = await dmSubmitPayment({
        esoId, pendingAmount: parsedAmount, description: description || undefined,
        paymentType, bankAccountId: accountId || undefined,
      })
      setResult(autoSubmit ?? null)
      setShowConfirm(false)
      onSubmitted()
      loadContext()
    } catch (e: any) {
      setShowConfirm(false)
      setSubmitError(e instanceof PaymentsApiError ? e.message : (e?.response?.data?.error ?? e?.message ?? "Submit failed"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Stack p={5} borderWidth="1px" borderRadius="lg" borderColor="purple.200" bg="purple.50" gap={3}>
      <HStack justifyContent="space-between" alignItems="center">
        <Text fontSize="md" fontWeight="bold" color="purple.800">
          Deal Manager — Submit Payment
        </Text>
        {contextLoading && <Spinner size="sm" />}
      </HStack>
      <Text fontSize="xs" color="gray.600">
        Visible to deal managers only. Stages the pending amount on this deal and submits it straight to ESO
        approval — the same flow as the OG&rsquo;s Set Pending Liquidity. All payment limits still apply.
      </Text>

      {contextError && (
        <Text fontSize="sm" color="red.600">{contextError}</Text>
      )}

      <HStack gap={6} flexWrap="wrap">
        <Stack gap={0}>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold">Deal Total</Text>
          <Text fontSize="sm" fontWeight="semibold">{formatDollar(total)}</Text>
        </Stack>
        <Stack gap={0}>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold">Released (Sent)</Text>
          <Text fontSize="sm" fontWeight="semibold">{formatDollar(sent)}</Text>
        </Stack>
        <Stack gap={0}>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold">Staged Pending</Text>
          <Text fontSize="sm" fontWeight="semibold">{formatDollar(staged)}</Text>
        </Stack>
        {dailyTotal && (
          <Stack gap={0}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold">Today&rsquo;s Wire Headroom</Text>
            <Text fontSize="sm" fontWeight="semibold">
              {formatDollar(dailyTotal.available_remaining)} <Text as="span" fontWeight="normal" color="gray.500">of {formatDollar(dailyTotal.daily_limit)}</Text>
            </Text>
          </Stack>
        )}
      </HStack>

      <Stack direction={{ base: "column", md: "row" }} gap={3} alignItems="flex-start">
        <Stack gap={1}>
          <Text fontSize="xs" color="gray.600" fontWeight="medium">Payment</Text>
          <Select fontSize="sm" h="8" px={3} borderRadius="md" bg="white" value={paymentType} onChange={(e) => setPaymentType(e.target.value as DmPaymentType)}>
            <option value="exercise">Exercise</option>
            <option value="liquidity">Liquidity</option>
          </Select>
        </Stack>
        <Stack gap={1} flex={1} w={{ base: "100%", md: "auto" }}>
          <Text fontSize="xs" color="gray.600" fontWeight="medium">Send to</Text>
          <Select fontSize="sm" h="8" px={3} borderRadius="md" bg="white" value={accountId} onChange={(e) => setAccountId(e.target.value)} disabled={!accounts.length}>
            {!accounts.length && <option value="">No account on file yet</option>}
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{accountLabel(a)}{a.is_default ? " — default" : ""}</option>
            ))}
          </Select>
        </Stack>
      </Stack>
      {toCompany && (
        <Text fontSize="xs" color="purple.800">
          Payment to the company on behalf of the client: it goes to {chosen?.account_holder_name ?? "the company"} and is never staged on the deal.
        </Text>
      )}

      <Stack direction={{ base: "column", md: "row" }} gap={3} alignItems="flex-start">
        <Stack gap={1}>
          <Text fontSize="xs" color="gray.600" fontWeight="medium">Amount (USD)</Text>
          <Input
            size="sm"
            bg="white"
            type="number"
            min={0}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />
        </Stack>
        <Stack gap={1} flex={1} w={{ base: "100%", md: "auto" }}>
          <Text fontSize="xs" color="gray.600" fontWeight="medium">Description (memo on the approval email)</Text>
          <Textarea
            size="sm"
            bg="white"
            rows={1}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Astranis Exercise 2,800 Shs"
          />
        </Stack>
        <Stack gap={1} pt="18px">
          <Button size="sm" variant="solid" disabled={!canSubmit} onClick={() => setShowConfirm(true)}>
            Stage &amp; Submit for Approval
          </Button>
        </Stack>
      </Stack>

      {blockers.map((b) => (
        <Text key={b} fontSize="xs" color={hasConnectedBank || b.startsWith("Client has no") ? "orange.700" : "red.600"}>
          {b}
        </Text>
      ))}

      {submitError && (
        <Text fontSize="sm" color="red.600" fontWeight="medium">✖ {submitError}</Text>
      )}
      {result && (
        result.submitted ? (
          <Text fontSize="sm" color="green.700" fontWeight="medium">
            ✔ {result.toCompany ? `Sent to ${result.payee?.name ?? "the company"} on behalf of the client, for ESO approval` : "Submitted for ESO approval"}
            {result.transactionNumber ? ` — transaction ${result.transactionNumber}` : ""}. Approval emails sent.
          </Text>
        ) : (
          <Text fontSize="sm" color="orange.700" fontWeight="medium">
            Amount staged, but not submitted: {result.reason}
          </Text>
        )
      )}

      {/* Confirm dialog */}
      <Dialog.Root open={showConfirm} onOpenChange={(e) => !e.open && !submitting && setShowConfirm(false)}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <VStack p={{ base: 5, md: 8 }} gap={4}>
                <Box borderRadius={5} bg="purple.500" p={3}>
                  <TiWarningOutline size={30} color="white" />
                </Box>
                <Text fontSize="xl" fontWeight="bold">Submit payment for approval?</Text>
                <Text textAlign="center" color="gray.700">
                  {toCompany ? (
                    <>
                      This sends <strong>{amountValid ? formatDollar(parsedAmount) : "$0"}</strong> to{" "}
                      <strong>{chosen?.account_holder_name ?? "the company"}</strong> on behalf of the client (deal #{esoId}) and
                      submits it to the ESO approval pipeline (approvers are emailed immediately).
                    </>
                  ) : (
                    <>
                      This stages <strong>{amountValid ? formatDollar(parsedAmount) : "$0"}</strong> on deal #{esoId} and
                      submits it to the ESO approval pipeline{chosen ? <> for <strong>{accountLabel(chosen)}</strong></> : null} (approvers are emailed immediately).
                    </>
                  )}
                </Text>
                <HStack gap={3} mt={2}>
                  <Button variant="outline" disabled={submitting} onClick={() => setShowConfirm(false)}>
                    Cancel
                  </Button>
                  <Button variant="solid" loading={submitting} onClick={handleSubmit}>
                    Confirm Submit
                  </Button>
                </HStack>
              </VStack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Stack>
  )
}
